/* eslint-disable react/prop-types */
import { FC, useEffect } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";

const selectClassName =
	"flex h-9 w-full items-center rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

interface Props {
	person: Person;
	onSave: (values: PersonFormValues) => void;
	isSaving: boolean;
	stores: LocationStore[];
}

const PersonDetailsTab: FC<Props> = ({ person, onSave, isSaving, stores }) => {
	const defaultValues: PersonFormValues = {
		name: person.name,
		email: person.email,
		role: person.role,
		employeeId: person.employeeId,
		store: person.store ? String(person.store.id) : ""
	};

	const methods = useForm<PersonFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit } = methods;
	const { errors, isDirty } = useFormState({ control });

	useEffect(() => {
		reset({
			name: person.name,
			email: person.email,
			role: person.role,
			employeeId: person.employeeId,
			store: person.store ? String(person.store.id) : ""
		});
	}, [person, reset]);

	const isSubmitDisabled = !isDirty || isSaving;

	return (
		<form
			onSubmit={handleSubmit(onSave)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors p-6",
							errors.name ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.name && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.name.message ?? "Please fill out the required field"}
								</span>
							</div>
						)}

						<label className="font-heading text-lg font-medium text-foreground mb-4 block">
							Details
						</label>

						<div className="flex flex-col gap-5">
							<FormField
								control={control}
								name="name"
								rules={{ required: "Name is required" }}
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Name
										</FormLabel>
										<FormControl>
											<Input
												placeholder="Full name"
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Email
										</FormLabel>
										<FormControl>
											<Input
												type="email"
												placeholder="Email address"
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="role"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Role
										</FormLabel>
										<FormControl>
											<Input
												placeholder="Role"
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="employeeId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Employee ID
										</FormLabel>
										<FormControl>
											<Input
												className="w-full bg-white"
												readOnly
												disabled
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="store"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-zinc-600">
											Store
										</FormLabel>
										<FormControl>
											<select className={selectClassName} {...field}>
												<option value="">None</option>
												{stores.map((store) => (
													<option key={store.id} value={String(store.id)}>
														{store.name}
													</option>
												))}
											</select>
										</FormControl>
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-end pt-6 mt-6 border-t border-border">
					<Button type="submit" disabled={isSubmitDisabled}>
						{isSaving ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default PersonDetailsTab;
